/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useRef, useState, useEffect } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { renderTextWithFootnotesReferencesV2 } from '../feature-service-types';
import { SplideSlide, SplideTrack, Splide } from '@splidejs/react-splide';

import {
  Text as AudiText,
  Dialog,
  Headline,
  Pagination,
  TextLink,
} from '@oneaudi/unified-web-components';
import styled from 'styled-components';

import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';

import { carouselState } from '../carouselState';
import { urlQueryStringToObj } from '../urlQueryStringToObj';
import { objToStr } from '../objToStr';

import { removeRole } from '../removeRole';
import { messages } from '../../i18n/messages';
import { mediaProperty } from '../mediaProperty';
import { WLTPComponent } from './Wltp';
import {
  generateIxisDataAttributes,
  generateNavigationTrackingAttributes,
  trackEvent,
} from '../tracking/IxisTracking';

import {
  SYS_BORDER_RADIUS_MD,
  SYS_PAGE_MARGIN,
  SYS_SPACE_FIXED_200,
} from '@oneaudi/unified-web-common';

const isLegacy = process.env.IS_LEGACY;
import { VideoPlayer } from './VideoPlayer';
import { processBodyText } from '../processBodyText';
import { ensureHtmlWrapped } from '../ensureHtmlWrapped';
// import { convertHtmlToReact } from '../convertHtmlToReact';

const Div = styled.div`
  a {
    span {
      gap: 0px;
    }
  }
  img {
    border-radius: ${isLegacy ? '0' : SYS_BORDER_RADIUS_MD};
  }
`;
const Span = styled.span`
  > div {
    padding: 0 ${SYS_PAGE_MARGIN} !important;
    overflow: hidden !important;

    @media (max-width: 374px) {
      padding: 0 ${SYS_PAGE_MARGIN} !important;
    }
    @media (min-width: 375px) and (max-width: 767px) {
      padding: 0 ${SYS_PAGE_MARGIN} !important;
    }
    @media (min-width: 768px) and (max-width: 1023px) {
      padding: 0 ${SYS_PAGE_MARGIN} !important;
    }
    @media (min-width: 1024px) and (max-width: 1439px) {
      padding: 0 ${SYS_PAGE_MARGIN} !important;
    }
  }
`;

const carouselSlides = (
  slides: ContentApp['slides'],
  slideWidth: number,
  slideHeight: number,
  refSlideTrack: HTMLDivElement | null,
  assetFormat: string,
  carouselWidth: number,
  carouselTitle: string | undefined,
) => {
  return slides.map((slide, index) => {
    const {
      slideAssetType,
      slideTitle,
      slideImageAssetPath = '',
      slideImageLandscapeAssetPath = '',
      slideImageAlt,
      slideVideoLandscapeAssetPath,
      slideVideoLandscapeCaption,
      slideVideoLandscapeAudioPath,
      slideVideoLandscapeAlt = '',
      slideVideoPortraitAssetPath,
      slideVideoPortraitCaption,
      slideVideoPortraitAudioPath,
      slideVideoPortraitAlt = '',
      slideVideoAutoPlay,
      slideVideoLoop,
      slideVideoPreserveVideoWidth,
      slideLandscapeAlt,
      slideDescription = '',
      slideDisclaimer,
      slideWltpKeys,
      slideCtaText,
      slideCtaUrl,
      slideCtaAriaLabel,
      slideCtaUrlTarget,
    } = slide;

    const { property, label } = mediaProperty(
      slideAssetType || 'image',
      assetFormat,
      carouselWidth,
    );

    const [open, setOpen] = useState(false);

    const [slideImageSrc, slideImageSrcQueryParams] = slideImageAssetPath.split('?');
    const slideImageSrcQueryParamsObj = urlQueryStringToObj(slideImageSrcQueryParams);
    slideImageSrcQueryParamsObj.preferwebp = 'true';
    slideImageSrcQueryParamsObj.width = `${slideWidth}`;
    const imgPortraitSrc = `${slideImageSrc}?${objToStr(slideImageSrcQueryParamsObj)}`;

    const [slideImageLandscapeSrc, slideImageLandscapeSrcQueryParams] =
      slideImageLandscapeAssetPath.split('?');
    const slideImageLandscapeSrcQueryParamsObj = urlQueryStringToObj(
      slideImageLandscapeSrcQueryParams,
    );
    slideImageLandscapeSrcQueryParamsObj.preferwebp = 'true';
    slideImageLandscapeSrcQueryParamsObj.width = `${slideWidth}`;
    const imgLandscapeSrc = `${slideImageLandscapeSrc}?${objToStr(slideImageLandscapeSrcQueryParamsObj)}`;

    const { key, imgSrc, alt } = (() => {
      const title = slideTitle || 'slideTitle';
      if (slideAssetType === 'video') {
        if (assetFormat === 'small' || carouselWidth <= 767) {
          return {
            key: `${title}:${slideVideoPortraitAlt || 'slideVideoPortraitAlt'}:${slideVideoPortraitAssetPath || 'slideVideoPortraitAssetPath'}:${index}`,
            imgSrc: '',
            alt: slideVideoPortraitAlt || 'slideVideoPortraitAlt',
          };
        } else {
          return {
            key: `${title}:${slideVideoLandscapeAlt || 'slideVideoLandscapeAlt'}:${slideVideoLandscapeAssetPath || 'slideVideoLandscapeAssetPath'}:${index}`,
            imgSrc: '',
            alt: slideVideoLandscapeAlt || 'slideVideoLandscapeAlt',
          };
        }
      }

      if (assetFormat === 'small' || carouselWidth <= 767) {
        const altText = slideImageAlt || slideLandscapeAlt || 'Image';
        return {
          key: `${title}:${altText}:${slideImageAssetPath || 'slideImageAssetPath'}:${index}`,
          imgSrc: imgPortraitSrc,
          alt: altText,
        };
      } else {
        const altText = slideLandscapeAlt || slideImageAlt || 'Image';
        return {
          key: `${title}:${altText}:${slideImageLandscapeAssetPath || 'slideImageLandscapeAssetPath'}:${index}`,
          imgSrc: imgLandscapeSrc,
          alt: altText,
        };
      }
    })();

    useEffect(() => {
      removeRole(refSlideTrack, `splide01-slide0${index + 1}`, 'role');
    }, [refSlideTrack]);

    const videoAsset = {
      assetFormat,
      videoUrlLargeViewport: slideVideoLandscapeAssetPath || '',
      videoUrlSmallViewport: slideVideoPortraitAssetPath || '',
      ueElementProperty: property,
      ueElementLabel: label,
      alternateDescription: slideVideoLandscapeAlt || '',
      preserveVideoWidth: slideVideoPreserveVideoWidth || false,
      loop: slideVideoLoop || false,
      closedCaption: slideVideoLandscapeCaption || '',
      autoPlay: slideVideoAutoPlay || false,
      alternateDescriptionPortrait: slideVideoPortraitAlt || '',
      closedCaptionPortrait: slideVideoPortraitCaption || '',
      audioDescription: slideVideoLandscapeAudioPath || '',
      audioDescriptionPortrait: slideVideoPortraitAudioPath || '',
    };

    const processedBodyText = processBodyText(ensureHtmlWrapped(slideDescription));

    const ixisDataAttributes = generateIxisDataAttributes({
      slideTitle,
      ctaText: slideCtaText,
      ctaLink: slideCtaUrl,
      altText: alt,
      position: index,
      bannerHeadline: carouselTitle,
    });

    const onCtaClick = (slideIndex: number, ctaText: string, ctaLink: string) => {
      trackEvent({
        eventType: 'cta_clicked',
        slideTitle: slides[slideIndex]?.slideTitle,
        ctaText,
        ctaLink,
        altText: alt,
        position: slideIndex,
        bannerHeadline: carouselTitle,
      });
    };

    const onDisclaimerView = (index: number) => {
      setOpen(!open);
      trackEvent({
        eventType: 'disclaimer_viewed',
        slideTitle: slides[index]?.slideTitle,
        ctaText: slides[index]?.slideCtaText,
        ctaLink: slides[index]?.slideCtaUrl,
        altText: alt,
        position: index,
        bannerHeadline: carouselTitle,
      });
    };

    return (
      <SplideSlide key={key}>
        <UeContainer propertyPath={`slides.${index}`} label="Slide" as="div">
          <UeReference propertyPath={`slides.${index}`} behavior="component">
            <div
              data-testid="CarouselSlide"
              className="faCarousel__slide"
              style={{ width: `${slideWidth}px` }}
              {...ixisDataAttributes}
            >
              <Div>
                {(slideAssetType === 'image' && (
                  <UeElement type="media" property={property} label={label}>
                    <img
                      data-testid="CarouselSlideImage"
                      className="faCarousel__image"
                      src={imgSrc}
                      alt={alt}
                      width={slideWidth}
                      height={slideHeight}
                    />
                  </UeElement>
                )) || <VideoPlayer asset={videoAsset} width={slideWidth} height={slideHeight} />}
                {(slideTitle || slideDescription) && (
                  <div className="faCarousel__copy">
                    {slideTitle && (
                      <UeElement type="text" property="slideTitle" label="Slide Title">
                        <Headline
                          variant={{ order: '3', weight: 'normal', type: 'headline' }}
                          tag="span"
                          data-testid="CarouselSlideTitle"
                          className="faCarousel__slideTitle"
                        >
                          {renderTextWithFootnotesReferencesV2(slideTitle)}
                        </Headline>
                      </UeElement>
                    )}

                    {slideDescription && (
                      <UeElement type="text" property="slideDescription" label="Slide Description">
                        <AudiText
                          tag="span"
                          variant={{ order: '1', style: 'normal', type: 'copy' }}
                          data-testid="CarouselSlideDescription"
                          className="faCarousel__slideDescription"
                        >
                          {processedBodyText}
                        </AudiText>
                      </UeElement>
                    )}
                  </div>
                )}

                {slideCtaUrl && slideCtaText && (
                  <UeElement type="text" property="slideCtaText" label="CTA Text">
                    <TextLink
                      data-testid="CarouselSlideCta"
                      aria-label={slideCtaAriaLabel || slideCtaText}
                      className="faCarousel__cta"
                      variant="standalone"
                      size={isLegacy ? 'md' : 'sm'}
                      href={slideCtaUrl}
                      target={slideCtaUrlTarget === '2' ? '_blank' : '_self'}
                      icon="forward"
                      iconPosition="trailing"
                      onClick={() => {
                        onCtaClick(index, slideCtaText, slideCtaUrl);
                      }}
                    >
                      {renderTextWithFootnotesReferencesV2(slideCtaText)}
                    </TextLink>
                  </UeElement>
                )}

                {slideDisclaimer && (
                  <Dialog
                    open={open}
                    onCloseButtonAriaLabel="Close"
                    onClose={() => {
                      setOpen(!open);
                    }}
                    variant="focus-large"
                    triggerButton={
                      <TextLink
                        data-testid="CarouselSlideCta"
                        variant="standalone"
                        size={isLegacy ? 'md' : 'sm'}
                        icon="forward"
                        iconPosition="trailing"
                        onClick={() => {
                          onDisclaimerView?.(index);
                        }}
                        className="faCarouselDisclaimer__cta"
                        margin={[0, 0, SYS_SPACE_FIXED_200, 0]}
                      >
                        {useI18n(messages.disclaimerCta)}
                      </TextLink>
                    }
                  >
                    <UeElement
                      type="text"
                      property="slideDisclaimer"
                      label="Disclaimer text displayed at bottom of slide in a smaller font size"
                    >
                      <AudiText
                        variant={{ order: '1', style: 'normal', type: 'copy' }}
                        data-testid="CarouselSlideDisclaimer"
                        className="faCarousel__slideDisclaimer"
                      >
                        {renderTextWithFootnotesReferencesV2(slideDisclaimer)}
                      </AudiText>
                    </UeElement>
                  </Dialog>
                )}

                {slideWltpKeys && slideWltpKeys.length > 0 && (
                  <WLTPComponent wltpTextValues={slideWltpKeys} theme={'light'} />
                )}
              </Div>
            </div>
          </UeReference>
        </UeContainer>
      </SplideSlide>
    );
  });
};

export const Carousel = ({ data }: { data: ContentApp }) => {
  const carouselSlidesLabel = useI18n(messages.carouselSlides);
  const imageCarouselLabel = useI18n(messages.carouselImage);
  const nextSlideLabel = useI18n(messages.nextSlide);
  const previousSlideLabel = useI18n(messages.previousSlide);
  const { carouselTitle, headlineHierarchy, slides, theme, assetFormat = 'small' } = data;

  const refSplide = useRef<Splide>(null);
  const refSlideTrack = useRef<HTMLDivElement>(null);

  const [carouselWidth, setCarouselWidth] = useState(0);
  const [carouselPadding, setCarouselPadding] = useState(0);
  const [slideWidth, setSlideWidth] = useState(300);
  const [slideHeight, setSlideHeight] = useState(300);
  const [slideActive, setSlideActive] = useState(0);
  const [slidesPerPage, setSlidesPerPage] = useState(1);
  const [paginationShow, setPaginationShow] = useState(true);
  const [paginationCount, setPaginationCount] = useState(1);
  const [paginationPage, setPaginationPage] = useState<number>(0);

  const slideGap = 16;

  const updateCarouselState = (padding: number, targetSlide?: number) => {
    if (!refSlideTrack?.current || !refSplide?.current?.splide) {
      throw new Error('Missing refSlideTrack or refSplide');
    }
    // Get new Carousel state based on width
    const out = carouselState({
      carouselWidth: refSlideTrack.current.clientWidth,
      carouselListWidth: refSlideTrack.current.querySelector('.splide__list')?.scrollWidth || 0,
      carouselPadding: padding,
      slideActive:
        typeof targetSlide === 'undefined' ? refSplide.current.splide.index : targetSlide,
      slidesTotal: slides.length,
      assetFormat: assetFormat,
    });

    // Update state
    setCarouselWidth(refSlideTrack.current.clientWidth);
    setSlideWidth(out.slideWidth);
    setSlideHeight(out.slideHeight);
    setSlideActive(out.slideActive);
    setSlidesPerPage(out.slidesPerPage);
    setPaginationCount(out.paginationCount);
    setPaginationPage(out.paginationPage);
    setPaginationShow(out.paginationShow);

    return out.slideActive;
  };

  // Connect Audi Pagination to Splide Carousel

  const onClickNext = () => {
    if (refSplide?.current?.splide) {
      const newSlide = updateCarouselState(
        carouselPadding,
        refSplide.current.splide.index + slidesPerPage,
      );
      // Move carousel
      refSplide.current.splide.go(newSlide);

      const currentSlideData = slides[newSlide];
      if (currentSlideData) {
        trackEvent({
          slideTitle: currentSlideData.slideTitle,
          eventType: 'next_clicked',
          ctaText: currentSlideData.slideCtaText,
          ctaLink: currentSlideData.slideCtaUrl,
          label: nextSlideLabel,
          position: newSlide,
          bannerHeadline: carouselTitle,
        });
      }
    }
  };

  const onClickPrev = () => {
    if (refSplide?.current?.splide) {
      const newSlide = updateCarouselState(
        carouselPadding,
        refSplide.current.splide.index - slidesPerPage,
      );
      // Move carousel
      refSplide.current.splide.go(newSlide);

      const currentSlideData = slides[newSlide];
      if (currentSlideData) {
        trackEvent({
          eventType: 'previous_clicked',
          slideTitle: currentSlideData.slideTitle,
          ctaText: currentSlideData.slideCtaText,
          ctaLink: currentSlideData.slideCtaUrl,
          label: previousSlideLabel,
          position: newSlide,
          bannerHeadline: carouselTitle,
        });
      }
    }
  };

  // Update Carousel state on resize
  useEffect(() => {
    if (refSplide?.current?.splide) {
      refSplide.current.splide.on('resized', () => {
        if (refSlideTrack?.current?.clientWidth !== carouselWidth) {
          updateCarouselState(carouselPadding);
        }
      });
    }
    // Cleanup
    return () => {
      if (refSplide?.current?.splide) {
        refSplide.current.splide.off('resized');
      }
    };
  }, [refSplide, carouselPadding]);

  useEffect(() => {
    if (refSplide?.current?.splide) {
      refSplide.current.splide.on('updated', ({ padding }) => {
        const { right = 0 } = padding as { [key: string]: number };
        setCarouselPadding(right);
      });
    }
    // Cleanup
    return () => {
      if (refSplide?.current?.splide) {
        refSplide.current.splide.off('updated');
      }
    };
  }, [refSplide]);

  // Update Carousel state on drag
  useEffect(() => {
    if (refSplide?.current?.splide) {
      refSplide.current.splide.on('active', () => {
        if (refSplide?.current?.splide) {
          updateCarouselState(carouselPadding, refSplide.current.splide.index);
        }
      });
    }
    return () => {
      if (refSplide?.current?.splide) {
        refSplide.current.splide.off('active');
      }
    };
  });

  // Update Carousel state on load
  useEffect(() => {
    updateCarouselState(carouselPadding);
    if (refSlideTrack && refSlideTrack.current) {
      let st = refSlideTrack.current.querySelector('ul')?.style.display;
      if (st) {
        st = 'flex';
      }
      // Fix list semantics for accessibility
      const ulElement = refSlideTrack.current.querySelector('.splide__list');
      if (ulElement) {
        ulElement.setAttribute('role', 'list');
        ulElement.setAttribute('aria-label', carouselSlidesLabel);

        const liElements = ulElement.querySelectorAll('.splide__slide');
        liElements.forEach((li) => {
          li.setAttribute('role', 'listitem');
        });
      }

      // Fix Splide container region role accessibility
      const splideContainer = refSlideTrack.current.querySelector('.splide');
      if (splideContainer) {
        splideContainer.setAttribute('aria-label', imageCarouselLabel);
      }
    }
  });

  useEffect(() => {
    const splideRegions = document.querySelectorAll('.splide[role="region"]');
    splideRegions.forEach((el) => {
      if (!el.getAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Image carousel');
      }
    });
  }, []);

  // Get class names
  const rootClassNames = [];

  if (theme) {
    const themeName = theme.toLowerCase();
    if (isLegacy) {
      if (themeName.includes('light')) {
        rootClassNames.push('faCarousel--light');
      }
      if (themeName.includes('dark')) {
        rootClassNames.push('faCarousel--dark');
      }
    }
  }
  rootClassNames.push(carouselTitle ? 'faCarousel--withTitle' : 'faCarousel--withoutTitle');

  // Get slides
  const splideSlides = carouselSlides(
    slides,
    slideWidth,
    slideHeight,
    refSlideTrack?.current,
    assetFormat,
    carouselWidth,
    carouselTitle,
  );

  return (
    <section
      data-testid="Carousel"
      aria-label={carouselTitle || imageCarouselLabel}
      aria-labelledby={carouselTitle ? 'CarouselTitle' : undefined}
      className={`faCarousel ${rootClassNames.join(' ')}`}
    >
      <UeElement type="text" property="carouselTitle" label="Title of Carousel">
        <Headline
          id="CarouselTitle"
          data-testid="CarouselTitle"
          className={`faCarousel__title ${carouselTitle ? 'faCarousel__title--set' : 'faCarousel__title--unset'}`}
          variant={{ order: '2', type: 'headline', weight: 'normal' }}
          tag={headlineHierarchy as 'h2' | 'h3' | 'h4'}
        >
          <span>{renderTextWithFootnotesReferencesV2(carouselTitle || '')}</span>
        </Headline>
      </UeElement>
      <div className="faCarousel__slideTrack" ref={refSlideTrack}>
        <Splide
          hasTrack={false}
          options={{
            pagination: false,
            arrows: false,
            gap: slideGap,
            autoWidth: true,
            fixedWidth: slideWidth,
            start: slideActive,
            breakpoints: {
              374: {
                padding: { left: 16, right: 16 },
              },
              767: {
                padding: { left: 28, right: 28 },
              },
              1023: {
                padding: { left: 40, right: 40 },
              },
              1439: {
                padding: { left: 40, right: 40 },
              },
              1919: {
                padding: { left: 96, right: 96 },
              },
              1920000: {
                padding: { left: 96, right: 96 },
              },
            },
          }}
          ref={refSplide}
        >
          <UeContainer propertyPath="slides" label="Carousel Slides">
            <Span>
              <SplideTrack>{splideSlides}</SplideTrack>
            </Span>
          </UeContainer>
        </Splide>
      </div>
      <div
        data-testid="CarouselPaginationContainer"
        className={`faCarousel__paginationContainer ${paginationShow ? 'faCarousel__paginationContainer--visible' : 'faCarousel__paginationContainer--hidden'}`}
      >
        <Pagination
          current={paginationPage}
          itemCount={paginationCount}
          data-testid="CarouselPagination"
          className="faCarousel__paginationComponent"
          variant="minimal"
          incrementElement={{
            'aria-label': nextSlideLabel,
            onClick: onClickNext,
            ...generateNavigationTrackingAttributes({
              position: slideActive,
              label: nextSlideLabel,
            }),
          }}
          decrementElement={{
            'aria-label': previousSlideLabel,
            onClick: onClickPrev,
            ...generateNavigationTrackingAttributes({
              position: slideActive,
              label: previousSlideLabel,
            }),
          }}
        />
      </div>
    </section>
  );
};
